/**
 * How a failed request is classified — the single most consequential rule in
 * this package, because it decides whether a customer's order waits or dies.
 */

/**
 * Statuses that mean "not yet", not "no". 401 is an expired access token: the
 * request is fine, the credential is not, and every app's `api.ts` clears its
 * tokens on it — treating it as a rejection would dead-letter a whole shift's
 * queued orders the moment a token aged out. 408 and 429 are explicit
 * invitations to try again.
 */
export const RETRYABLE_4XX: ReadonlySet<number> = new Set([401, 408, 429]);

/** The HTTP status an axios-shaped rejection carries, if it carries one. */
export const statusOf = (error: any): number | null =>
  typeof error?.response?.status === 'number' ? error.response.status : null;

/**
 * A 4xx the server will give again tomorrow — this entry is never going to
 * land, so retrying it forever would block every order behind it.
 */
export function isRejection(error: any): boolean {
  const status = statusOf(error);
  return typeof status === 'number' && status >= 400 && status < 500 && !RETRYABLE_4XX.has(status);
}

/** The server's own words, so a dead letter explains itself. */
export function failureMessage(error: any): string {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join('\n');
  if (typeof message === 'string' && message) return message;
  if (typeof error?.message === 'string' && error.message) return error.message;
  return 'Request failed';
}

/**
 * Thrown for an `onlineOnly` mutation attempted without connectivity.
 *
 * §17 / §28 — there is no offline outcome to queue for a card, wallet or
 * bank-transfer payment: the money either moved or it did not, and a replay
 * hours later would authorize against a card the guest has long since put away.
 * Those calls fail loudly instead of queueing.
 */
export class OnlineRequiredError extends Error {
  constructor(message = 'This action needs a live connection') {
    super(message);
    this.name = 'OnlineRequiredError';
  }
}
